import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink, Handshake, Loader2, MapPin } from 'lucide-react';
import { fetchVitrineSponsors } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { avecArticle } from '../utils/nomAssociation';
import LogoReseau from '../components/LogoReseau';

/**
 * Vitrine publique des sponsors et des partenaires institutionnels.
 *
 * La Mairie et la Communauté de communes ne signent pas de bon de parrainage :
 * elles subventionnent, prêtent du matériel, ferment une rue. Les remercier au
 * même endroit que les entreprises est la moindre des choses, et le titre
 * comme la phrase d'accroche doivent le dire.
 *
 * Un sponsor donne pour être vu : cette page lui rend cette visibilité. Chaque
 * fiche montre son visuel publicitaire en bandeau, son logo à cheval dessous —
 * la disposition d'un profil, qui fait ressortir la marque sans écraser le
 * visuel — puis ses coordonnées d'entreprise et ses réseaux.
 *
 * Ne paraissent que les données d'entreprise. Le nom du contact, son e-mail et
 * son téléphone restent au bureau : ils ont été donnés pour traiter un
 * dossier, pas pour figurer en ligne.
 */
/**
 * Retire un article initial du nom, pour que la phrase pose le sien.
 *
 * La phrase dit « à la … » — les deux partenaires du catalogue, mairie et
 * communauté de communes, sont féminins. Un nom saisi « La Mairie de … »
 * donnerait sinon « à la La Mairie ».
 */
function sansArticle(nom: string): string {
  return nom.replace(/^(la|le|les|l')\s*/i, '');
}

export default function NosSponsors() {
  const perso = usePersonnalisation();
  // Le nom de l'association, et non « la fête locale » : toutes les
  // associations ne font pas la fête, et un club de handball qui lit cette
  // phrase se demande de quelle fête on lui parle.
  const nomAssociation = avecArticle(perso?.nomAssociation);

  const { data, isPending } = useQuery({
    queryKey: ['vitrine-sponsors'],
    queryFn: fetchVitrineSponsors,
  });

  const sponsors: any[] = data?.sponsors ?? [];
  const partenaires: any[] = data?.partenaires ?? [];

  // Les partenaires ouvrent la liste, les sponsors suivent par ordre
  // alphabétique — l'API les rend déjà triés. Les mêler serait reléguer la
  // Mairie au rang d'une entreprise dont le nom commence par M.
  const cartes = [
    ...partenaires.map((p) => ({ ...p, id: `partenaire-${p.code}`, partenaire: true })),
    ...sponsors.map((s) => ({ ...s, id: `sponsor-${s.id}`, partenaire: false })),
  ];

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Nos sponsors &amp; partenaires
        </h1>
        {/* Les partenaires sont nommés tels que la section « Mairie & CC »
            des paramètres les a enregistrés, et seulement s'ils le sont :
            remercier en dur une collectivité au mauvais nom serait pire que
            ne pas la nommer. */}
        {/* « Sans eux, pas de fête » ne vaut que pour un comité des fêtes : un
            club de handball n'en organise aucune. La phrase dit ce qui est
            vrai de toute association qui vit de ses soutiens. */}
        <p className="mt-2 text-sm lg:text-base text-gray-600 max-w-3xl dark:text-gray-400">
          Sans eux, nous n'existerions pas. Merci
          {partenaires.map((p) => ` à la ${sansArticle(p.nom)},`).join('')} aux
          entreprises et aux commerçants qui nous soutiennent.
        </p>
      </div>

      {isPending ? (
        <div className="p-8 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : cartes.length === 0 ? (
        <div className="card lg:p-6 text-center space-y-3">
          <Handshake className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-600 dark:text-gray-400">
            Aucun sponsor à présenter pour le moment.
          </p>
          <Link to="/sponsors" className="btn-primary inline-flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Devenir le premier
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {cartes.map((c) => (
            <CarteSponsor key={c.id} sponsor={c} />
          ))}
        </div>
      )}

      <section className="card lg:p-6 text-center space-y-3">
        <p className="font-semibold text-gray-900 dark:text-gray-100">
          Vous aussi, soutenez {nomAssociation}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Votre logo sur le livret, les écocups et les t-shirts, et votre fiche sur cette page.
        </p>
        <Link to="/sponsors" className="btn-primary inline-flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Devenir sponsor
        </Link>
      </section>
    </div>
  );
}

function CarteSponsor({ sponsor: s }: { sponsor: any }) {
  return (
    <article className="card p-0 overflow-hidden flex flex-col">
      {/* Bandeau : le visuel publicitaire, ou un aplat quand il manque —
          mieux vaut une carte régulière qu'un trou dans la grille. */}
      <div className="relative">
        <div className="h-32 lg:h-36 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/40 dark:to-primary-800/40">
          {s.visuel && (
            <img
              src={s.visuel}
              alt={`Visuel publicitaire de ${s.nom}`}
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Le logo chevauche le bas du bandeau, au premier plan. */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-8">
          <div className="w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-md flex items-center justify-center overflow-hidden">
            {s.logo ? (
              <img
                src={s.logo}
                alt={`Logo de ${s.nom}`}
                className="w-full h-full object-contain p-1.5"
              />
            ) : (
              <Building2 className="w-7 h-7 text-gray-300" />
            )}
          </div>
        </div>
      </div>

      <div className="pt-11 px-4 pb-4 flex-1 flex flex-col text-center">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">{s.nom}</h2>

        {/* La pastille dit ce que la place en tête de liste laisse seulement
            deviner : ce n'est pas une entreprise qui a acheté un encart. */}
        {s.partenaire && (
          <p className="mt-1">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
              Partenaire institutionnel
            </span>
          </p>
        )}

        {s.adresse && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 inline-flex items-start justify-center gap-1">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
            <span className="whitespace-pre-line">{s.adresse}</span>
          </p>
        )}

        {s.siteWeb && (
          <a
            href={s.siteWeb}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-sm text-primary-700 dark:text-primary-400 hover:underline inline-flex items-center justify-center gap-1 break-all"
          >
            {s.siteWeb.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
        )}

        {/* Pied : les logos seuls, sans libellé — la marque se reconnaît. */}
        {s.reseaux?.length > 0 && (
          <div className="mt-auto pt-4 flex flex-wrap justify-center gap-2">
            {s.reseaux.map((r: any) => (
              <a
                key={r.reseau}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`${s.nom} sur ${r.label}`}
                aria-label={`${s.nom} sur ${r.label}`}
                className="w-9 h-9 rounded-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center hover:shadow-md transition"
              >
                <LogoReseau reseau={r.reseau} taille={18} />
              </a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
